import { css } from '@emotion/react'
import {
  Alert,
  Button,
  Cell,
  Heading,
  HFlow,
  Icon,
  Modal,
  ModalBody,
  ModalFooter,
  Text,
  Theme,
  Tooltip,
  useTheme,
  VFlow,
} from 'bold-ui'

interface ModalClassificacaoVulnerabilidadeProps {
  isModalOpen: boolean
  onClose(): void
}

export function ModalClassificacaoVulnerabilidade(props: Readonly<ModalClassificacaoVulnerabilidadeProps>) {
  const { isModalOpen, onClose } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  return (
    <Modal size='large' open={isModalOpen} closeOnBackdropClick={true} onClose={onClose}>
      <ModalBody>
        <VFlow vSpacing={1}>
          <HFlow
            justifyContent='space-between'
            alignItems='flex-start'
            style={{ marginTop: '0.25rem', marginRight: '-1rem' }}
          >
            <HFlow alignItems='center' hSpacing={0.5}>
              <Icon icon='infoCircleOutline' size={3} />
              <Text fontWeight='bold' component='span' style={{ fontSize: '1.5rem' }}>
                Estratificação de risco Familiar no e-SUS APS
              </Text>
            </HFlow>

            <Tooltip text='Fechar'>
              <Button onClick={onClose} skin='ghost' size='small' style={styles.closeBtn}>
                <span style={{ display: 'block', transform: 'translateY(-2px)' }}>&times;</span>
              </Button>
            </Tooltip>
          </HFlow>

          <VFlow>
            <Heading level={4}>Como o cálculo funciona?</Heading>
            <Text>
              O e-SUS APS apresenta automaticamente a Estratificação de Vulnerabilidade Familiar para todas as famílias
              cadastradas. Essa informação apoia a equipe na identificação de situações que demandam maior atenção e na
              organização do cuidado no território.
            </Text>

            <>
              <Text fontWeight='bold'>A vulnerabilidade é um fenômeno:</Text>
              <ul style={{ marginTop: 0, marginBottom: 0, paddingLeft: '1.3rem' }} css={styles.list}>
                <li>Multidimensional: envolve saúde, condições sociais e moradia.</li>
                <li>Dinâmico: pode mudar ao longo do tempo.</li>
                <li>Relacionado ao território e às desigualdades sociais.</li>
              </ul>
            </>

            <Cell css={styles.alertWrapper}>
              <Alert type='info'>
                Para calcular o risco de vulnerabilidade familiar é necessário manter o cadastro atualizado
              </Alert>
            </Cell>
          </VFlow>

          <VFlow>
            <Heading level={4}>Como o sistema define a vulnerabilidade familiar?</Heading>
            <Text>
              O cálculo é feito automaticamente a partir dos dados já registrados no cadastro da família e dos cidadãos.
              O sistema considera dois tipos de informação:
            </Text>

            <Text>
              <Text fontWeight='bold' component='span'>
                1 - Critérios inequívocos de vulnerabilidade:
              </Text>{' '}
              A presença de qualquer um deles já classifica a família como em vulnerabilidade, sem necessidade de
              aplicar a Escala de Vulnerabilidade Familiar de Coelho-Savassi.
            </Text>

            <Text>
              <Text fontWeight='bold' component='span'>
                2 - Critérios sugestivos de vulnerabilidade:
              </Text>{' '}
              Quando presentes, o sistema aplica automaticamente a Escala de Vulnerabilidade Familiar de Coelho-Savassi,
              classificando a família conforme o escore:
              <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.3rem' }} css={styles.list}>
                <li>Vulnerabilidade habitual: Escore 0 a 4</li>
                <li>Vulnerabilidade baixa: Escore 5 ou 6</li>
                <li>Vulnerabilidade média: Escore 7 ou 8</li>
                <li>Vulnerabilidade máxima: Escore 9 ou maior</li>
              </ul>
            </Text>
          </VFlow>

          <VFlow>
            <Heading level={4}>Como isso apoia o trabalho da equipe?</Heading>

            <VFlow vSpacing={0.5}>
              <Text>Com essa informação a equipe pode:</Text>
              <ul style={{ marginTop: 0, marginBottom: 0, paddingLeft: '1.3rem' }} css={styles.list}>
                <li>Priorizar visitas domiciliares.</li>
                <li>Organizar melhor a agenda.</li>
                <li>Identificar famílias que precisam de mais acompanhamento.</li>
              </ul>
            </VFlow>

            <Text>
              Manter o cadastro atualizado é essencial para acompanhar as mudanças ao longo do tempo. A classificação
              apoia o trabalho da equipe e não substitui a avaliação profissional sobre a realidade das famílias.
            </Text>
          </VFlow>
        </VFlow>
      </ModalBody>

      <ModalFooter style={styles.footer}>
        <HFlow justifyContent='flex-end'>
          <Button onClick={onClose} kind='primary'>
            Entendi
          </Button>
        </HFlow>
      </ModalFooter>
    </Modal>
  )
}

const createStyles = (theme: Theme) => ({
  footer: css`
    background-color: ${theme.pallete.gray.c90};
    border-top: 1px solid ${theme.pallete.gray.c80};
    padding: 1rem 1.5rem;
    border-radius: 0 0 0.5rem 0.5rem;
  `,
  alertWrapper: css`
    width: 75%;
    margin-top: 0.0625rem;
    margin-bottom: 1.75rem;

    &&& > div {
      border: 1.4px solid ${theme.pallete.primary.c60};
      border-radius: 0.25rem;
    }
  `,
  closeBtn: css`
    width: 48px;
    height: 32px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.25rem;
    color: ${theme.pallete.gray.c10};
    font-size: 2rem;
    font-weight: 200;
    font-family: 'Helvetica', 'Arial', sans-serif;

    &:hover {
      background-color: ${theme.pallete.gray.c90};
      color: ${theme.pallete.gray.c10};
    }
  `,
  list: css`
    & li::marker {
      font-size: 0.8rem;
    }
  `,
})
